import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { postMarkConversationRead } from './api';
import { isConversationRead } from '../../utils/conversationUtil';
import useDelayedCallback from '../utils/useDelayedCallback';
/**
 * @var How long we should wait until a conversation is marked as completed
 * */
var LAST_SEEN_TIMEOUT_MS = 500;
/**
 * Will mark the conversation as seen after a fixed delay,
 * the same action will be triggered after a new message is received
 * and the conversation is in focus.
 * */
var useMarkConversationSeen = function (params) {
    var _a;
    var conversation = params.conversation, user = params.user, markSeenTimeoutMs = params.markSeenTimeoutMs;
    var queryClient = useQueryClient();
    var slug = conversation === null || conversation === void 0 ? void 0 : conversation.slug;
    var mutation = useMutation(['conversation', slug], function (body) { return postMarkConversationRead(body.slug); }, {
        onSuccess: function () {
            // Make sure react-query will re-fetch the list of conversations
            queryClient.invalidateQueries(['conversations']);
        },
        onError: function (error) {
            console.error('Failed to mark conversation as read', error);
        },
    });
    // Determines whether we should mark the message as seen or not
    var hasUserUnseenMessages = (conversation && user) ? !isConversationRead(conversation, user) : false;
    // Ensures the delay is reset when the number of messages changes
    var numberOfMessages = ((_a = conversation === null || conversation === void 0 ? void 0 : conversation.messages) === null || _a === void 0 ? void 0 : _a.length) || 0;
    var markMessageAsSeen = useCallback(function () {
        if (!slug) {
            // If the conversation isn't visible yet, then there is nothing to do yet.
            return;
        }
        if (!hasUserUnseenMessages) {
            return;
        }
        mutation.mutate({ slug: slug });
        // numberOfMessages isn't used inside the function, but is
        // a dependency we require to reset useDelayedCallback when a new messages is added to the conversation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, numberOfMessages, hasUserUnseenMessages]);
    // Tests are allowed to override the default timeout
    var markSeenLatencyMs = markSeenTimeoutMs !== null && markSeenTimeoutMs !== void 0 ? markSeenTimeoutMs : LAST_SEEN_TIMEOUT_MS;
    useDelayedCallback(markMessageAsSeen, { delayTimeMs: markSeenLatencyMs });
};
export default useMarkConversationSeen;
